import type { SessionUser } from '@voice2me/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ApiError, api } from '../api/client.js';

export const sessionQueryKey = ['session'] as const;

/** Resolves the signed-in user, or null when signed out — 401 is an answer here, not an error. */
export function useSession() {
  return useQuery({
    queryKey: sessionQueryKey,
    queryFn: async (): Promise<SessionUser | null> => {
      try {
        return await api.get<SessionUser>('/auth/me');
      } catch (error) {
        if (error instanceof ApiError && error.status === 401) return null;
        throw error;
      }
    },
  });
}

export function useLogout() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => api.post('/auth/logout'),
    onSuccess: () => queryClient.setQueryData(sessionQueryKey, null),
  });
}

export function useRequestMagicLink() {
  return useMutation({
    mutationFn: (input: { email: string; redirectTo?: string }) =>
      api.post<{ ok: true }>('/auth/magic-link', input),
  });
}

/**
 * Redeems the emailed magic link. Called from the sign-in confirmation page on an explicit click,
 * so mail scanners that pre-fetch the link cannot spend the single-use token first.
 */
export function useVerifyMagicLink() {
  return useMutation({
    mutationFn: (input: { token: string }) =>
      api.post<{ ok: true; redirectTo: string }>('/auth/verify', input),
  });
}

/**
 * Names the survey a magic link leads to, for the confirmation page. Read-only — it does not spend
 * the token. Returns nulls when the link has no survey destination (e.g. an admin sign-in).
 */
export function useMagicLinkPreview(token: string | null) {
  return useQuery({
    queryKey: ['magic-link-preview', token],
    enabled: Boolean(token),
    queryFn: () =>
      api.get<{ surveyTitle: string | null; vendorName: string | null }>(
        `/auth/verify/preview?token=${encodeURIComponent(token as string)}`,
      ),
  });
}

/** Password sign-in for the consultancy admin portal. Refetches the session so the UI updates. */
export function useAdminLogin() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: { email: string; password: string }) =>
      api.post<{ ok: true }>('/auth/admin-login', input),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: sessionQueryKey }),
  });
}
